import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Activity, ArrowRight, LogIn, ShieldAlert, ShieldCheck, Users, type LucideIcon } from "lucide-react";
import { Card, Skeleton } from "@platform/ui";
import { useApi, useConsole } from "../context";
import { useT } from "../i18n";
import { formatDateTime, formatNumber } from "../lib/format";
import { ErrorBanner, PageCard } from "../layout/Page";

function LineChart({ data, label }: { data: Array<{ date: string; count: number }>; label: string }) {
  const w = 600;
  const h = 180;
  const pad = 28;
  const max = Math.max(1, ...data.map((d) => d.count));
  const x = (i: number) => pad + (i * (w - pad * 2)) / Math.max(1, data.length - 1);
  const y = (v: number) => h - pad - (v * (h - pad * 2)) / max;
  const path = data.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(d.count).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label} className="h-auto w-full">
      <title>{label}</title>
      {[0, 0.5, 1].map((f) => (
        <g key={f}>
          <line
            x1={pad}
            x2={w - pad}
            y1={y(max * f)}
            y2={y(max * f)}
            stroke="rgb(15 23 42 / 0.08)"
            strokeDasharray={f ? "3 4" : undefined}
          />
          <text x={pad - 6} y={y(max * f) + 4} textAnchor="end" fontSize="10" fill="rgb(15 23 42 / 0.5)">
            {Math.round(max * f)}
          </text>
        </g>
      ))}
      <defs>
        <linearGradient id="chart-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#009be5" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#009be5" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${path} L${x(data.length - 1)},${h - pad} L${x(0)},${h - pad} Z`} fill="url(#chart-fill)" />
      <path
        d={path}
        fill="none"
        stroke="#009be5"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {data.map((d, i) => (
        <g key={d.date}>
          <circle cx={x(i)} cy={y(d.count)} r="2.5" fill="#fff" stroke="#009be5" strokeWidth="1.5">
            <title>{`${d.date}: ${d.count}`}</title>
          </circle>
          {i % 2 === 0 && (
            <text x={x(i)} y={h - 8} textAnchor="middle" fontSize="10" fill="rgb(15 23 42 / 0.5)">
              {d.date.slice(5)}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

export function OverviewPage() {
  const { t, locale } = useT();
  const api = useApi();
  const { can, config } = useConsole();
  const stats = useQuery({ queryKey: ["stats"], queryFn: api.stats });
  const recent = useQuery({
    queryKey: ["logs", "recent"],
    queryFn: () => api.logs.list({ limit: 8 }),
    enabled: can("logs:read"),
  });
  const s = stats.data;
  const tiles: Array<[string, string, LucideIcon, string]> = s
    ? [
        [t("overview.totalUsers"), formatNumber(s.totalUsers, locale), Users, "bg-info-bg text-primary-dark"],
        [t("overview.active7d"), formatNumber(s.active7d, locale), Activity, "bg-success-bg text-success"],
        [
          t("overview.signInsToday"),
          formatNumber(s.signInsToday, locale),
          LogIn,
          "bg-info-bg text-primary-dark",
        ],
        [
          t("overview.failed24h"),
          formatNumber(s.failedSignIns24h, locale),
          ShieldAlert,
          "bg-danger-bg text-danger",
        ],
        [t("overview.mfaAdoption"), `${s.mfaAdoptionPercent}%`, ShieldCheck, "bg-success-bg text-success"],
      ]
    : [];
  return (
    <div className="mx-auto flex max-w-[var(--container-content)] flex-col gap-6">
      {stats.isError && (
        <Card>
          <ErrorBanner error={stats.error} onRetry={() => void stats.refetch()} />
        </Card>
      )}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {stats.isPending
          ? Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="h-28" />)
          : tiles.map(([label, value, Icon, tint]) => (
              <Card key={label} className="flex flex-col p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[13px] leading-snug text-ink-2">{label}</p>
                  <span
                    aria-hidden
                    className={`inline-flex size-8 shrink-0 items-center justify-center rounded-lg ${tint}`}
                  >
                    <Icon className="size-4" />
                  </span>
                </div>
                <p className="mt-auto pt-3 text-3xl font-semibold tracking-tight tabular-nums">{value}</p>
              </Card>
            ))}
      </div>
      <PageCard className="p-6">
        <h2 className="mb-4 text-base font-semibold">{t("overview.chartTitle")}</h2>
        {s ? (
          <LineChart data={s.signInsPerDay} label={t("overview.chartLabel")} />
        ) : (
          <Skeleton className="h-44" />
        )}
      </PageCard>
      {can("logs:read") && (
        <PageCard>
          <div className="flex items-center px-6 pt-5">
            <h2 className="flex-1 text-base font-semibold">{t("overview.recent")}</h2>
            <Link
              to="/audit-logs"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-dark hover:underline"
            >
              {t("overview.viewAll")}
              <ArrowRight aria-hidden className="size-3.5" />
            </Link>
          </div>
          <ul className="divide-y divide-line px-6 py-3">
            {recent.data?.items.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center gap-x-4 py-2.5 text-sm">
                <span className="rounded-md bg-subtle px-1.5 py-0.5 font-mono text-xs text-ink ring-1 ring-line ring-inset">
                  {e.type}
                </span>
                <span className="flex-1 truncate font-mono text-xs text-ink-2">{e.subjectUserId ?? ""}</span>
                <time className="text-xs text-ink-2" dateTime={e.occurredAt}>
                  {formatDateTime(e.occurredAt, locale, config.timeZone)}
                </time>
              </li>
            ))}
            {recent.isPending && <Skeleton className="my-2 h-20" />}
          </ul>
        </PageCard>
      )}
    </div>
  );
}
